/**
 * reorder — 排序交互共用的纯函数（`~/ui/SortableList` 与各页的草稿排序都走这里）
 *
 * 全部不改入参、返回新数组；越界 / 原地不动返回 `null`，调用方据此忽略这次操作。
 */

/** 把 `items[from]` 挪到下标 `to`，其余顺势平移。 */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] | null {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return null;
  const next = [...items];
  const [picked] = next.splice(from, 1);
  next.splice(to, 0, picked as T);
  return next;
}

/**
 * 把 `moving` 放到 `before` 之前；`before` 为 `null` = 追加到末尾。
 * 拖拽落点语义：落在某项上 = 放在它之前，末位放置区 = 追加。
 */
export function moveItemBefore<T>(items: readonly T[], moving: T, before: T | null): T[] | null {
  const from = items.indexOf(moving);
  if (from < 0 || moving === before) return null;
  const rest = items.filter((item) => item !== moving);
  const to = before === null ? rest.length : rest.indexOf(before);
  if (to < 0) return null;
  rest.splice(to, 0, moving);
  return rest.every((item, index) => item === items[index]) ? null : rest;
}

/**
 * 只重排「可排序」子序列：`items` 里满足 `isSortable` 的元素占据的槽位保持不变，
 * 槽位上的元素按 `reorderedSortable` 的新顺序回填；不可排序的元素原位不动。
 * 用于「同一列表里只有部分项有位次」的场景（如已上架 / 未上架混排、按板块分段）。
 */
export function refillSortableSlots<T>(
  items: readonly T[],
  isSortable: (item: T) => boolean,
  reorderedSortable: readonly T[],
): T[] {
  let cursor = 0;
  return items.map((item) => (isSortable(item) ? (reorderedSortable[cursor++] as T) : item));
}
